import type { ItineraryItemEventType } from './schemas/itinerary-item-event-schemas.js';

/**
 * Pure, presentation-agnostic ruleset that maps Google Place types to a
 * `PlaceCategory` and to the discriminated-union `ItineraryItemEventType` used
 * by the itinerary view.
 *
 * This module is the single source of truth shared by the server (write-time
 * denormalization in `upsertPlace()`) and the web client (fallback during
 * migration). Icons and labels are per-client presentation and intentionally
 * live in the client, not here.
 */

/**
 * Place categories derived from Google Place types.
 * Used to expand generic `place` itinerary items into categorized events.
 */
export type PlaceCategory = 'dining' | 'culture' | 'entertainment' | 'nature' | 'shopping' | 'wellness' | 'nightlife';

/** Priority-ordered list of categories (higher priority first). */
const CATEGORY_PRIORITY: PlaceCategory[] = [
  'dining',
  'culture',
  'entertainment',
  'nature',
  'shopping',
  'wellness',
  'nightlife',
];

/** Map from Google Place type string to PlaceCategory. */
const TYPE_TO_CATEGORY = new Map<string, PlaceCategory>([
  // Dining
  ['acai_shop', 'dining'],
  ['afghani_restaurant', 'dining'],
  ['african_restaurant', 'dining'],
  ['american_restaurant', 'dining'],
  ['argentinian_restaurant', 'dining'],
  ['asian_fusion_restaurant', 'dining'],
  ['asian_restaurant', 'dining'],
  ['australian_restaurant', 'dining'],
  ['austrian_restaurant', 'dining'],
  ['bagel_shop', 'dining'],
  ['bakery', 'dining'],
  ['bangladeshi_restaurant', 'dining'],
  ['bar', 'dining'],
  ['bar_and_grill', 'dining'],
  ['barbecue_restaurant', 'dining'],
  ['basque_restaurant', 'dining'],
  ['bavarian_restaurant', 'dining'],
  ['belgian_restaurant', 'dining'],
  ['bistro', 'dining'],
  ['brazilian_restaurant', 'dining'],
  ['breakfast_restaurant', 'dining'],
  ['british_restaurant', 'dining'],
  ['brunch_restaurant', 'dining'],
  ['buffet_restaurant', 'dining'],
  ['burmese_restaurant', 'dining'],
  ['burrito_restaurant', 'dining'],
  ['cafe', 'dining'],
  ['cafeteria', 'dining'],
  ['cajun_restaurant', 'dining'],
  ['cake_shop', 'dining'],
  ['californian_restaurant', 'dining'],
  ['cambodian_restaurant', 'dining'],
  ['candy_store', 'dining'],
  ['cantonese_restaurant', 'dining'],
  ['caribbean_restaurant', 'dining'],
  ['cat_cafe', 'dining'],
  ['chicken_restaurant', 'dining'],
  ['chicken_wings_restaurant', 'dining'],
  ['chilean_restaurant', 'dining'],
  ['chinese_noodle_restaurant', 'dining'],
  ['chinese_restaurant', 'dining'],
  ['chocolate_shop', 'dining'],
  ['coffee_roastery', 'dining'],
  ['coffee_shop', 'dining'],
  ['coffee_stand', 'dining'],
  ['colombian_restaurant', 'dining'],
  ['confectionery', 'dining'],
  ['croatian_restaurant', 'dining'],
  ['cuban_restaurant', 'dining'],
  ['czech_restaurant', 'dining'],
  ['danish_restaurant', 'dining'],
  ['deli', 'dining'],
  ['dessert_restaurant', 'dining'],
  ['dessert_shop', 'dining'],
  ['dim_sum_restaurant', 'dining'],
  ['diner', 'dining'],
  ['dog_cafe', 'dining'],
  ['donut_shop', 'dining'],
  ['dumpling_restaurant', 'dining'],
  ['dutch_restaurant', 'dining'],
  ['eastern_european_restaurant', 'dining'],
  ['ethiopian_restaurant', 'dining'],
  ['european_restaurant', 'dining'],
  ['falafel_restaurant', 'dining'],
  ['family_restaurant', 'dining'],
  ['fast_food_restaurant', 'dining'],
  ['filipino_restaurant', 'dining'],
  ['fine_dining_restaurant', 'dining'],
  ['fish_and_chips_restaurant', 'dining'],
  ['fondue_restaurant', 'dining'],
  ['food', 'dining'],
  ['food_court', 'dining'],
  ['french_restaurant', 'dining'],
  ['fusion_restaurant', 'dining'],
  ['gastropub', 'dining'],
  ['german_restaurant', 'dining'],
  ['greek_restaurant', 'dining'],
  ['gyro_restaurant', 'dining'],
  ['halal_restaurant', 'dining'],
  ['hamburger_restaurant', 'dining'],
  ['hawaiian_restaurant', 'dining'],
  ['hot_dog_restaurant', 'dining'],
  ['hot_dog_stand', 'dining'],
  ['hot_pot_restaurant', 'dining'],
  ['hungarian_restaurant', 'dining'],
  ['ice_cream_shop', 'dining'],
  ['indian_restaurant', 'dining'],
  ['indonesian_restaurant', 'dining'],
  ['irish_restaurant', 'dining'],
  ['israeli_restaurant', 'dining'],
  ['italian_restaurant', 'dining'],
  ['japanese_curry_restaurant', 'dining'],
  ['japanese_izakaya_restaurant', 'dining'],
  ['japanese_restaurant', 'dining'],
  ['juice_shop', 'dining'],
  ['kebab_shop', 'dining'],
  ['korean_barbecue_restaurant', 'dining'],
  ['korean_restaurant', 'dining'],
  ['latin_american_restaurant', 'dining'],
  ['lebanese_restaurant', 'dining'],
  ['malaysian_restaurant', 'dining'],
  ['meal_delivery', 'dining'],
  ['meal_takeaway', 'dining'],
  ['mediterranean_restaurant', 'dining'],
  ['mexican_restaurant', 'dining'],
  ['middle_eastern_restaurant', 'dining'],
  ['mongolian_barbecue_restaurant', 'dining'],
  ['moroccan_restaurant', 'dining'],
  ['noodle_shop', 'dining'],
  ['north_indian_restaurant', 'dining'],
  ['oyster_bar_restaurant', 'dining'],
  ['pakistani_restaurant', 'dining'],
  ['pastry_shop', 'dining'],
  ['persian_restaurant', 'dining'],
  ['peruvian_restaurant', 'dining'],
  ['pizza_delivery', 'dining'],
  ['pizza_restaurant', 'dining'],
  ['polish_restaurant', 'dining'],
  ['portuguese_restaurant', 'dining'],
  ['ramen_restaurant', 'dining'],
  ['restaurant', 'dining'],
  ['romanian_restaurant', 'dining'],
  ['russian_restaurant', 'dining'],
  ['salad_shop', 'dining'],
  ['sandwich_shop', 'dining'],
  ['scandinavian_restaurant', 'dining'],
  ['seafood_restaurant', 'dining'],
  ['shawarma_restaurant', 'dining'],
  ['snack_bar', 'dining'],
  ['soul_food_restaurant', 'dining'],
  ['soup_restaurant', 'dining'],
  ['south_american_restaurant', 'dining'],
  ['south_indian_restaurant', 'dining'],
  ['southwestern_us_restaurant', 'dining'],
  ['spanish_restaurant', 'dining'],
  ['sri_lankan_restaurant', 'dining'],
  ['steak_house', 'dining'],
  ['sushi_restaurant', 'dining'],
  ['swiss_restaurant', 'dining'],
  ['taco_restaurant', 'dining'],
  ['taiwanese_restaurant', 'dining'],
  ['tapas_restaurant', 'dining'],
  ['tea_house', 'dining'],
  ['tex_mex_restaurant', 'dining'],
  ['thai_restaurant', 'dining'],
  ['tibetan_restaurant', 'dining'],
  ['tonkatsu_restaurant', 'dining'],
  ['turkish_restaurant', 'dining'],
  ['ukrainian_restaurant', 'dining'],
  ['vegan_restaurant', 'dining'],
  ['vegetarian_restaurant', 'dining'],
  ['vietnamese_restaurant', 'dining'],
  ['western_restaurant', 'dining'],
  ['yakiniku_restaurant', 'dining'],
  ['yakitori_restaurant', 'dining'],

  // Culture
  ['art_gallery', 'culture'],
  ['art_museum', 'culture'],
  ['art_studio', 'culture'],
  ['auditorium', 'culture'],
  ['buddhist_temple', 'culture'],
  ['castle', 'culture'],
  ['church', 'culture'],
  ['cultural_center', 'culture'],
  ['cultural_landmark', 'culture'],
  ['fountain', 'culture'],
  ['hindu_temple', 'culture'],
  ['historical_landmark', 'culture'],
  ['historical_place', 'culture'],
  ['history_museum', 'culture'],
  ['library', 'culture'],
  ['monument', 'culture'],
  ['mosque', 'culture'],
  ['museum', 'culture'],
  ['performing_arts_theater', 'culture'],
  ['place_of_worship', 'culture'],
  ['sculpture', 'culture'],
  ['shinto_shrine', 'culture'],
  ['synagogue', 'culture'],

  // Entertainment
  ['amusement_center', 'entertainment'],
  ['amusement_park', 'entertainment'],
  ['aquarium', 'entertainment'],
  ['bowling_alley', 'entertainment'],
  ['comedy_club', 'entertainment'],
  ['community_center', 'entertainment'],
  ['concert_hall', 'entertainment'],
  ['convention_center', 'entertainment'],
  ['dog_park', 'entertainment'],
  ['event_venue', 'entertainment'],
  ['ferris_wheel', 'entertainment'],
  ['live_music_venue', 'entertainment'],
  ['miniature_golf_course', 'entertainment'],
  ['movie_theater', 'entertainment'],
  ['observation_deck', 'entertainment'],
  ['opera_house', 'entertainment'],
  ['planetarium', 'entertainment'],
  ['plaza', 'entertainment'],
  ['skateboard_park', 'entertainment'],
  ['stadium', 'entertainment'],
  ['tourist_attraction', 'entertainment'],
  ['video_arcade', 'entertainment'],
  ['vineyard', 'entertainment'],
  ['visitor_center', 'entertainment'],
  ['water_park', 'entertainment'],
  ['wildlife_park', 'entertainment'],
  ['wildlife_refuge', 'entertainment'],
  ['zoo', 'entertainment'],

  // Nature
  ['beach', 'nature'],
  ['botanical_garden', 'nature'],
  ['campground', 'nature'],
  ['city_park', 'nature'],
  ['garden', 'nature'],
  ['hiking_area', 'nature'],
  ['island', 'nature'],
  ['lake', 'nature'],
  ['marina', 'nature'],
  ['mountain_peak', 'nature'],
  ['national_park', 'nature'],
  ['natural_feature', 'nature'],
  ['nature_preserve', 'nature'],
  ['park', 'nature'],
  ['picnic_ground', 'nature'],
  ['river', 'nature'],
  ['scenic_spot', 'nature'],
  ['ski_resort', 'nature'],
  ['state_park', 'nature'],
  ['woods', 'nature'],

  // Shopping
  ['asian_grocery_store', 'shopping'],
  ['bicycle_store', 'shopping'],
  ['book_store', 'shopping'],
  ['butcher_shop', 'shopping'],
  ['cell_phone_store', 'shopping'],
  ['chocolate_factory', 'shopping'],
  ['clothing_store', 'shopping'],
  ['convenience_store', 'shopping'],
  ['cosmetics_store', 'shopping'],
  ['department_store', 'shopping'],
  ['discount_store', 'shopping'],
  ['discount_supermarket', 'shopping'],
  ['electronics_store', 'shopping'],
  ['farmers_market', 'shopping'],
  ['flea_market', 'shopping'],
  ['furniture_store', 'shopping'],
  ['gift_shop', 'shopping'],
  ['grocery_store', 'shopping'],
  ['hardware_store', 'shopping'],
  ['health_food_store', 'shopping'],
  ['home_goods_store', 'shopping'],
  ['home_improvement_store', 'shopping'],
  ['hypermarket', 'shopping'],
  ['jewelry_store', 'shopping'],
  ['liquor_store', 'shopping'],
  ['market', 'shopping'],
  ['pet_store', 'shopping'],
  ['shoe_store', 'shopping'],
  ['shopping_mall', 'shopping'],
  ['sporting_goods_store', 'shopping'],
  ['sportswear_store', 'shopping'],
  ['store', 'shopping'],
  ['supermarket', 'shopping'],
  ['tea_store', 'shopping'],
  ['thrift_store', 'shopping'],
  ['toy_store', 'shopping'],
  ['warehouse_store', 'shopping'],
  ['womens_clothing_store', 'shopping'],

  // Wellness
  ['barber_shop', 'wellness'],
  ['beauty_salon', 'wellness'],
  ['fitness_center', 'wellness'],
  ['gym', 'wellness'],
  ['hair_care', 'wellness'],
  ['hair_salon', 'wellness'],
  ['massage', 'wellness'],
  ['massage_spa', 'wellness'],
  ['nail_salon', 'wellness'],
  ['sauna', 'wellness'],
  ['skin_care_clinic', 'wellness'],
  ['spa', 'wellness'],
  ['tanning_studio', 'wellness'],
  ['wellness_center', 'wellness'],
  ['yoga_studio', 'wellness'],

  // Nightlife
  ['beer_garden', 'nightlife'],
  ['brewery', 'nightlife'],
  ['brewpub', 'nightlife'],
  ['casino', 'nightlife'],
  ['cocktail_bar', 'nightlife'],
  ['dance_hall', 'nightlife'],
  ['hookah_bar', 'nightlife'],
  ['internet_cafe', 'nightlife'],
  ['irish_pub', 'nightlife'],
  ['karaoke', 'nightlife'],
  ['lounge_bar', 'nightlife'],
  ['night_club', 'nightlife'],
  ['pub', 'nightlife'],
  ['sports_bar', 'nightlife'],
  ['wine_bar', 'nightlife'],
  ['winery', 'nightlife'],
]);

/**
 * Classify an array of Google Place types into a single PlaceCategory.
 * Returns undefined if no types match any known category.
 *
 * Uses priority ordering so that, e.g., a place with both `restaurant` and `museum`
 * resolves to `dining` (higher priority).
 */
export function classifyPlaceTypes(googleTypes: string[]): PlaceCategory | undefined {
  if (!googleTypes || googleTypes.length === 0) return undefined;

  // Collect all matched categories
  const matched = new Set<PlaceCategory>();
  for (const t of googleTypes) {
    const cat = TYPE_TO_CATEGORY.get(t);
    if (cat) matched.add(cat);
  }

  if (matched.size === 0) return undefined;

  // Return highest-priority match
  for (const cat of CATEGORY_PRIORITY) {
    if (matched.has(cat)) return cat;
  }

  return undefined;
}

/**
 * Human-readable label for a raw Google place type — `irish_pub` → "Irish pub", `art_gallery` →
 * "Art gallery". Used to name a place event by what it actually is rather than by the broad category
 * it falls into ("Nightlife" for a pub, "Dining" for a bakery).
 *
 * Returns undefined for a blank type, and for `place`/`establishment`/`point_of_interest` — Google's
 * catch-alls, which say nothing the category label doesn't.
 */
export function formatPlaceTypeLabel(placeType: string | undefined): string | undefined {
  if (!placeType) return undefined;
  const words = placeType.replaceAll(/[_-]+/g, ' ').trim().toLowerCase();
  if (words === '' || GENERIC_PLACE_TYPES.has(words)) return undefined;
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** Google types too generic to label an event with — they carry no more meaning than the category. */
const GENERIC_PLACE_TYPES = new Set(['place', 'establishment', 'point of interest', 'premise']);

/** Drinking venues Google classifies outside dining. */
export const DRINKING_VENUE_TYPES: ReadonlySet<string> = new Set([
  'beer_garden',
  'brewery',
  'brewpub',
  'cocktail_bar',
  'irish_pub',
  'lounge_bar',
  'pub',
  'sports_bar',
  'vineyard',
  'wine_bar',
  'winery',
]);

/**
 * Types outside the `dining` category that still mean the traveler is eating or drinking out. Google
 * files pubs, wine bars, and taprooms under nightlife (and a vineyard under entertainment) while
 * putting `bar`, `bar_and_grill`, and `gastropub` under dining — a split we can't do much about, so
 * these are named explicitly.
 *
 * The inns are here because of what a `place` itinerary item means: somewhere the traveler is GOING.
 * Where they sleep is a `room` item, so an inn on the itinerary is the dining room — a country inn or
 * a pub with rooms — and a ryokan's kaiseki dinner is the reason for the visit in the first place.
 *
 * Deliberately excluded: `night_club`, `casino`, `karaoke`, `dance_hall`, `hookah_bar`,
 * `internet_cafe` — an evening at any of those leaves dinner still to sort out — and
 * `bed_and_breakfast`, which settles breakfast, not the day.
 */
const NON_DINING_MEAL_TYPES = new Set([
  ...DRINKING_VENUE_TYPES,
  'budget_japanese_inn',
  'inn',
  'japanese_inn',
]);

/**
 * Whether a booking at this place type means the day's food and drink is already taken care of — the
 * whole `dining` category, plus the drink-led venues Google files elsewhere ({@link
 * NON_DINING_MEAL_TYPES}).
 *
 * Used to decide whether the itinerary still needs to suggest somewhere to eat that day. Judged on the
 * place's own type rather than its broad category, so a pub counts and a nightclub doesn't.
 */
export function placeTypeServesMeal(placeType: string | undefined): boolean {
  if (!placeType) return false;
  const normalized = placeType.trim().toLowerCase();
  return TYPE_TO_CATEGORY.get(normalized) === 'dining' || NON_DINING_MEAL_TYPES.has(normalized);
}

/**
 * Resolve a place's Google types into a specific place event type.
 * Prefers `primaryType` (the single most representative type from Google) when available,
 * falling back to scanning the full `types` array.
 * Falls back to `'place'` when no category matches.
 */
export function resolvePlaceEventType(googleTypes: string[], primaryType?: string | null): ItineraryItemEventType {
  // Prefer primaryType — it's Google's canonical pick for the place
  if (primaryType) {
    const cat = TYPE_TO_CATEGORY.get(primaryType);
    if (cat) return `place_${cat}` as ItineraryItemEventType;
  }
  const category = classifyPlaceTypes(googleTypes);
  if (!category) return 'place';
  return `place_${category}` as ItineraryItemEventType;
}
